/**
 * Base dictionary. Every key is born here; other locales are checked against it.
 *
 * Style: short declarative sentences. State what a thing is and what an action
 * will do. No exclamation marks, no persuasion. Where the product cannot do
 * something, the text says so plainly instead of going quiet.
 */

export const en = {
  // ---------- common ----------
  'common.brand': 'gdsgate',
  'common.console': 'console',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.retry': 'Read it again',
  'common.signOut': 'Sign out',
  'common.loading': 'Loading',
  'common.actions': 'Actions',
  'common.dash': '—',
  'common.copy': 'Copy',
  'common.copied': 'Copied.',
  'time.ago': '{value} ago',
  'common.why': 'Why',
  'common.unread': 'Not read',
  'table.expand': 'More',
  'table.collapse': 'Less',
  'common.copyByHand':
    'This browser would not let the page copy it. Select the text and copy it by hand.',

  // ---------- shell ----------
  'nav.skipToContent': 'Skip to content',
  'nav.primary': 'Primary',
  'nav.group.fleet': 'Fleet',
  'nav.group.tenancy': 'Tenancy',
  'nav.group.account': 'Account',
  'nav.account.tenants': 'Tenancies',
  'nav.account.tenancy': 'My tenancy',
  'nav.account.usage': 'Consumption',
  'nav.account.rollout': 'Versions',
  'nav.account.identityProvider': 'Identity provider',
  'nav.account.password': 'Password',
  'nav.cluster.group': 'Cluster',
  'nav.cluster.catalog': 'Catalogue',
  'shell.role.providerAdmin': 'Provider operator',
  'shell.role.providerSupport': 'Support, read-only',
  'shell.role.tenantAdmin': 'Tenancy representative',
  'shell.role.cluster': 'Cluster administrator',
  'shell.scope.provider': 'All tenancies',
  'shell.scope.provider.sub': 'provider',
  'shell.breadcrumb': 'Breadcrumb',

  'topbar.theme.toLight': 'Switch to the light theme',
  'topbar.theme.toDark': 'Switch to the dark theme',
  'topbar.language': 'Language',
  'topbar.language.en': 'English',
  'topbar.language.ru': 'Русский',

  // ---------- account sign-in ----------
  'login.heading': 'Sign in to your account',
  'login.subtitle':
    'The provider’s account: tenancies, their state, and how people sign in to your cluster.',
  'login.email.label': 'Email',
  'login.email.placeholder': 'you@example.com',
  'login.password.label': 'Password',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in',
  'login.failed': 'Sign-in failed',
  'login.badCredentials':
    'The address and the password do not go together. Which of the two is wrong is deliberately not said: any difference here is a way to find out which addresses exist.',
  'login.noReset':
    'There is no reset by mail: this product sends no mail. A password is set from the command line, by somebody already on the host.',
  'login.check.emailRequired': 'Enter an email address.',
  'login.check.emailInvalid': 'This is not an email address.',
  'login.check.passwordRequired': 'Enter a password.',
  'login.tooMany.title': 'Too many attempts',
  'login.tooMany.body':
    'The server counts failures against this address inside a window, and the window is full. It empties on its own.',
  'login.tooMany.wait': 'Try again in {wait}.',
  'login.tooMany.unknownWait':
    'How long is left is not in the answer, and the console will not guess. The window is minutes rather than hours.',
  'login.tooMany.rightPassword':
    'The correct password does not get through the window either. A counter that let the right guess through would not be a counter.',
  'login.quote':
    'Identity-aware, audited, least-privilege access to every host, database and cluster — behind one proxy.',
  'login.quote.by': 'one binary · policy-checked · recorded',
  'login.terminal.comment': '# after one login your native tools just work',
  'login.terminal.login': 'gdsgate login',
  'login.terminal.ssh': 'ssh app-prod-03',
  'login.terminal.ssh.note': '→ via gdsgate',
  'login.terminal.psql': 'psql -h orders-db',
  'login.terminal.psql.note': '→ policy-checked',
  'login.terminal.kubectl': 'kubectl get pods',
  'login.terminal.kubectl.note': '→ recorded',

  // ---------- own password ----------
  'password.title': 'Password',
  'password.section': 'Change the password',
  'password.forAccount': 'For {email}.',
  'password.current.label': 'Current password',
  'password.current.hint':
    'Asked for even though you are already signed in: a stolen session must not be enough to keep the account.',
  'password.next.label': 'New password',
  'password.next.hint':
    'At least {least} characters. Length is the only requirement that buys anything here.',
  'password.repeat.label': 'New password again',
  'password.submit': 'Change the password',
  'password.submitting': 'Changing',
  'password.check.currentRequired': 'Enter the current password.',
  'password.check.currentWrong': 'This is not the current password.',
  'password.check.tooShort': 'At least {least} characters.',
  'password.check.sameAsCurrent': 'This is the password already in use.',
  'password.check.mismatch': 'The two do not match.',
  'password.check.refused': 'The server would not take this password.',
  'password.effect.title': 'What changing it does',
  'password.effect.sessions':
    'Every session of this account closes, including this one: a session that outlived the password would be the thing the change was meant to close.',
  'password.effect.noReset':
    'There is no way back through mail. Forgetting the new password means asking somebody with access to the host to set another.',

  // ---------- cluster sign-in ----------
  'clusterLogin.heading': 'Sign in to the cluster',
  'clusterLogin.subtitle':
    'This console belongs to {domain}. It admits people through the identity provider this tenancy chose — a provider account opens nothing here.',
  'clusterLogin.provider.title': 'This cluster admits people through',
  'clusterLogin.provider.ours': 'the provider’s identity provider',
  'clusterLogin.provider.byProvider':
    'The identity provider the provider runs. Your people are enrolled and revoked by the provider.',
  'clusterLogin.provider.byTenancy':
    'This tenancy’s own identity provider. Your people are enrolled and revoked by you.',
  'clusterLogin.continue': 'Continue to your identity provider',
  'clusterLogin.continuing': 'Starting',
  'clusterLogin.continue.hint':
    'You are handed to your provider and returned here. What a return is checked against stays on the server, behind a cookie only this browser has.',
  'clusterLogin.since': 'In this state since {when}.',
  'clusterLogin.reconfiguring.title': 'Sign-in is being reconfigured',
  'clusterLogin.reconfiguring.body':
    'The cluster is moving to another identity provider. No new session is issued until it finishes; sessions already open are not affected.',
  'clusterLogin.notConfigured.title': 'No identity provider has been applied',
  'clusterLogin.notConfigured.body':
    'The cluster is up, and nobody can sign in to it until this tenancy’s choice has been applied.',
  'clusterLogin.refused.title': 'Signed in, and this cluster does not admit you',
  'clusterLogin.refused.body':
    'Your identity provider vouched for you, and this tenancy’s policy does not name you an administrator of this cluster. No session was opened.',
  'clusterLogin.refused.group':
    'The group this cluster looks for was fixed when the tenancy was declared and does not move. Whoever runs your identity provider puts you in it.',
  'clusterLogin.badTarget.title': 'The answer named no address to go to',
  'clusterLogin.badTarget.body':
    'Starting the sign-in produced something that is not a provider’s address, so this console did not follow it. Nothing has happened.',
  'clusterLogin.account.title': 'The choice is made in the account',
  'clusterLogin.account.body':
    'It is on the provider’s domain and is entered with a provider account — so that a cluster nobody can sign in to can still be put right.',
  'clusterLogin.account.open': 'Open the account',

  // ---------- dead ends ----------
  'unknownRole.title': 'This console does not know this role',
  'unknownRole.body':
    'The account carries a role this console was not built for. Guessing at the nearest one would show controls the server refuses.',
  'unknownRole.account': 'Signed in as {email}.',
  'clusterNoAccess.title': 'Signed in, with nothing to administer',
  'clusterNoAccess.body':
    'The identity provider vouched for you, and you are in none of the groups this cluster grants rights to. Whoever runs that provider adds you to the group it looks for.',
  'didNotStart.title': 'This console did not start',
  'didNotStart.body':
    'Which console an address serves is decided by the control plane, and it did not answer. Nothing here can decide that on its own: a guess would show one tenant the console of another.',
  'didNotStart.detail': 'What the browser got back: {detail}',
  'didNotStart.retry': 'Try again',

  // ---------- tenancies ----------
  'tenants.title': 'Tenancies',
  'tenants.create': 'Declare a tenancy',
  'tenants.search.label': 'Search tenancies',
  'tenants.search.placeholder': 'Name, slug, domain',
  'tenants.filter.label': 'Filter by state',
  'tenants.filter.all': 'All',
  'tenants.col.name': 'Tenancy',
  'tenants.col.domain': 'Domain',
  'tenants.col.state': 'State',
  'tenants.col.declared': 'Declared state',
  'tenants.col.version': 'Version',
  'tenants.col.created': 'Declared',
  'tenants.declared.told': 'Told',
  'tenants.declared.never': 'never',
  'tenants.empty.title': 'No tenancies match',
  'tenants.empty.body': 'Change the filter or the search text.',
  'tenants.none.title': 'No tenancies here',
  'tenants.none.body':
    'An account sees the tenancies it is entitled to. An empty list is an answer, not a failure.',
  'tenants.summary.total': 'Tenancies',
  'tenants.summary.ready': 'Handed over',
  'tenants.summary.behind': 'Not told yet',
  'tenants.summary.failed': 'Not accepted',

  // ---------- tenancy lifecycle ----------
  'tenantState.requested': 'Declared',
  'tenantState.requested.hint': 'Declared, and nothing has been applied to it yet.',
  'tenantState.provisioning': 'Provisioning',
  'tenantState.provisioning.hint':
    'The desired state is being applied. The tenancy is not handed over until the diagnosis passes.',
  'tenantState.ready': 'Handed over',
  'tenantState.ready.hint':
    'The last application ran every step and the diagnosis exited zero on every role.',
  'tenantState.failed': 'Not accepted',
  'tenantState.failed.hint':
    'The last application did not finish, or the diagnosis came back non-zero. The cluster may well be serving; it is not accepted.',
  'tenantState.suspended': 'Suspended',
  'tenantState.suspended.hint':
    'Deliberately stopped. The data is still there, and applying the desired state is refused: it would put back what was taken away.',
  'tenantState.tearingDown': 'Being taken apart',
  'tenantState.tearingDown.hint': 'Being taken apart, in the order of the provisioning contract.',
  'tenantState.removed': 'Taken apart',
  'tenantState.removed.hint':
    'Taken apart. The row stays, so that the slug and the domain are not handed to somebody else by accident.',
  'tenantState.unknown': 'Unknown state',
  'tenantState.unknown.hint': 'The control plane reports a state this console does not know.',

  // ---------- one tenancy ----------
  'tenant.back': 'All tenancies',
  'tenant.section.fingerprint': 'First contact',
  'tenant.section.idp': 'Identity provider',
  'tenant.field.slug': 'Slug',
  'tenant.field.consoleDomain': 'Console domain',
  'tenant.field.gatewayDomain': 'Gateway',
  'tenant.field.adminGroup': 'Administrator group',
  'tenant.field.state': 'State',
  'tenant.field.version': 'gdsgate version',
  'tenant.field.created': 'Declared',
  'tenant.field.updated': 'Last changed',
  'tenant.adminGroup.note':
    'Fixed when the tenancy was declared. It is substituted into the policy the cluster is seeded with, and the control plane cannot rewrite it — so it does not move when the identity provider does.',
  'tenant.fingerprint.none':
    'No fingerprint yet: one appears once a gateway has run. It is the first thing a client of this tenancy needs.',
  'tenant.fingerprint.note':
    'The one thing in a client’s first contact that does not come from whoever answered. It goes to gdsgate connect beside the domain.',
  'tenant.declared.behind': 'The cluster has not been told about some of this',
  'tenant.idp.absent': 'The answer carried no identity provider for this tenancy.',
  'tenant.idp.applying':
    'A change of identity provider is running. Until it finishes, this tenancy’s people cannot obtain a new session.',
  'tenant.idp.failed':
    'The cluster was left on what it ran before. The tenancy puts this right from its own account.',
  'tenant.idp.note':
    'Shown, not set. No route changes a tenancy’s identity provider by slug, for anyone: an operator who could point a customer’s cluster at a provider could point it at one whose tokens they mint.',
  'tenant.catalog.note':
    'Shown, not edited. A catalogue is written in that cluster’s own console, so that the change carries a person’s name into the tenancy’s own audit trail.',
  'tenant.notFound.title': 'No such tenancy',
  'tenant.notFound.body':
    'There is no tenancy by that name, or this account is not entitled to it. The answer is the same either way, on purpose.',
  'tenant.section.overview': 'Overview',
  'tenant.sections.legend': 'Section of this tenancy',
  'tenant.tile.generation': 'Declared / applied',

  // ---------- one's own tenancy ----------
  'tenancy.title': 'My tenancy',
  'tenancy.tile.resources': 'Resources in the catalogue',
  'tenancy.certificate.until': 'Certificate until {when}',
  'tenancy.addresses.title': 'Addresses',
  'tenancy.version.title': 'Version',
  'tenancy.version.untold': 'The board carries no line for this tenancy.',

  // ---------- consumption ----------
  'usage.title': 'Consumption',
  'usage.back': 'Back to the tenancy',
  'usage.period.from.short': 'From',
  'usage.period.to.short': 'To',
  'usage.period.hint':
    'Both ends are required and neither is filled in for you. The window is half-open: an event exactly on the end belongs to the next period.',
  'usage.period.utc': 'Each day is read as its midnight in UTC.',
  'usage.period.lastMonth': 'Last month',
  'usage.period.thisMonth': 'This month',
  'usage.submit': 'Read the figures',
  'usage.submitting': 'Reading',
  'usage.again': 'Read them again',
  'usage.notPolled':
    'Nothing here refreshes on its own. Two of the four figures are questions put to the tenancy’s own cluster, and that cluster records the asking under the name of whoever asked — a screen on a timer would fill a customer’s audit trail with this console’s curiosity.',
  'usage.check.fromRequired': 'Choose the day the period starts.',
  'usage.check.toRequired': 'Choose the day the period ends.',
  'usage.check.fromUnreadable': 'This is not a date.',
  'usage.check.toUnreadable': 'This is not a date.',
  'usage.check.order': 'The end has to be after the start.',

  'usage.figure.connectors': 'Connectors enrolled',
  'usage.figure.resources': 'Resources served',
  'usage.figure.sessions': 'Sessions opened in the period',
  'usage.figure.people': 'People who signed in during the period',

  'usage.asOf': 'As of {when}. Not a period figure.',
  'usage.asOf.why':
    'Connectors and resources are counted as of the moment they were read: gdsgate’s node registry and resource catalogue are current state with no history in them. Sessions and people are the two counted over the window.',

  'usage.missing.title': 'Not counted',
  'usage.missing.body':
    'An absent figure is not a zero: zero is a number an invoice can be written from. Each one shows the reason the server gave, in place of a number.',
  'usage.missing.noReason':
    'The answer left this figure out and named no reason for it. Nothing here fills the hole with a number.',
  'usage.missing.normal':
    'It says a source could not be read, not that nothing happened. Sessions and people are counted out of the tenancy’s exported audit chain, and where the control plane cannot reach that export they arrive missing.',
  'usage.gap.unknown': 'A figure this console does not draw was not counted either: {figure}.',

  'usage.connectors.section': 'Connectors',
  'usage.connectors.revoked': 'Revoked identities',
  'usage.connectors.revoked.hint':
    'Not billed. It is here because “we revoked it” is the answer to “why did the other number fall”.',
  'usage.connectors.notOnline':
    'Not connectors on line: gdsgate has no signal that says a tunnel is up, so this counts identities in the registry. The tenancy’s gateway and the control plane’s own identity are registry rows too, and neither is counted.',
  'usage.connectors.col.id': 'Registry id',
  'usage.connectors.col.claimed': 'The name the node gave itself',
  'usage.connectors.col.enrolled': 'Enrolled',
  'usage.connectors.claimed.mark': 'claimed by the node, not verified',
  'usage.connectors.claimed.hint':
    'A provision token is issued for a role and not for a machine, so there is nothing in this name for a cluster to check. That is why the mark is beside the name in every row rather than in a footnote.',
  'usage.connectors.claimed.none': 'The node named itself nothing.',
  'usage.connectors.enrolled.unknown': 'The registry printed a stamp that is not an instant.',
  'usage.connectors.none': 'No live connector identity is enrolled for this tenancy.',

  'usage.resources.note':
    'The cluster’s catalogue, not the one declared here. The two differ until a change has been applied, and counting the declared one would charge for resources nobody can reach yet.',
  'usage.resources.byKind': 'By kind',

  'usage.sessions.count.hint':
    'A session belongs to the window it opened in and to that one only, with its whole length.',
  'usage.sessions.closed': 'Of those, closed',
  'usage.sessions.unclosed': 'Still open, or their close never arrived',
  'usage.sessions.unclosed.hint':
    'Counted above — the opening row is proof the session happened — and contributing nothing to the length below. “Now minus the opening” would bill for a session that may have ended weeks ago.',
  'usage.sessions.duration': 'Length of the sessions that closed',
  'usage.sessions.duration.parts': '{hours} h {minutes} min {seconds} s',
  'usage.sessions.recorded': 'Session recording written',
  'usage.sessions.recorded.hint':
    'Bytes of recording, and not bytes relayed. A session to a database, into Kubernetes, to an MCP server or over plain TCP closes at zero however much traffic crossed it. Nothing in the chain measures relayed volume.',
  'usage.sessions.orphan': 'Closes with no opening',
  'usage.sessions.orphan.hint':
    'In none of the other figures: with no opening there is no start to date the session by and no length to measure. Published so that a total which is short says so.',
  'usage.sessions.byKind': 'Opened, by protocol',

  'usage.people.count.hint':
    'Distinct people. One person is one, however many times they signed in.',
  'usage.people.rows': 'Those people account for {rows} sign-in rows.',
  'usage.people.rows.short': 'sign-in rows: {rows}',
  'usage.people.rows.hint':
    'Rows are not people, and no arithmetic turns them into people. gdsgate files at most one row per person per day per Auth process, so a restart or a second instance adds rows without adding anybody.',

  'usage.howCounted': 'How these are counted',
  'usage.bytes': 'bytes',
  'usage.gaps.title': 'What these figures do not cover',
  'usage.gaps.relay':
    'Relayed volume. Nothing in a tenancy’s audit chain measures it, so no figure here is traffic.',
  'usage.gaps.node':
    'A node that joined and was revoked inside the period. The registry holds one row per identity with no history, so nothing says when it was active.',
  'usage.gaps.resource':
    'A resource declared and removed inside the period. The catalogue is a set rather than a log.',
  'usage.gaps.online':
    'Connectors on line. gdsgate publishes no signal that a connector’s tunnel is up, so enrolled is as far as the connector figure goes.',
  'usage.gaps.unclosed':
    'The length of a session that has not closed, and a close whose opening was never written. Both are counted on their own above rather than folded into the totals.',

  'usage.notNameable.title': 'Your provider names you in a form the cluster will not record',
  'usage.notNameable.body':
    'Nothing was read. Two of these figures are asked of the tenancy’s own cluster, which records the asking under the name of whoever asked, and gdsgate takes a name of letters, digits, spaces and . _ - @ + ’ only. It is put right at the identity provider that names you.',

  // ---------- versions and rolling one out ----------
  'rollout.title': 'Versions and rolling out',

  'rollout.none.title': 'No rollout is under way',
  'rollout.start': 'Start a rollout',
  'rollout.stop': 'Stop the rollout',
  'rollout.field.actor': 'Started by',
  'rollout.field.finished': 'Ended',
  'rollout.field.finished.untold':
    'The answer did not say when. Nothing here puts another moment in its place.',
  'rollout.field.changed': 'State last moved',

  'rollout.tally.moved': 'Moved',
  'rollout.tally.failed': 'Not moved',
  'rollout.tally.pending': 'Not reached yet',
  'rollout.tally.crosses': 'Named breaks crossed',
  'rollout.tally.notWorkedOut': 'Not worked out',
  'rollout.tally.tenants': 'Tenancies on the board',
  'rollout.tally.declared': 'Declared versions',
  'rollout.tally.broken': 'With a named break',
  'rollout.tally.notMovable': 'In no wave',

  'rollout.filter.wave': 'Filter by wave',
  'rollout.filter.wave.all': 'All',
  'rollout.filter.step': 'Filter by move',
  'rollout.filter.step.all': 'All',
  'rollout.search.label': 'Search the board',
  'rollout.search.placeholder': 'Slug',

  'rollout.col.tenant': 'Tenancy',
  'rollout.col.state': 'State',
  'rollout.col.wave': 'Wave',
  'rollout.col.declared': 'Declared',
  'rollout.col.authority': 'authority',
  'rollout.col.proxy': 'proxy',
  'rollout.col.pairs': 'Pairs',
  'rollout.col.observed': 'Read',
  'rollout.col.step': 'Move',
  'rollout.observed.never': 'Never',
  'rollout.tenant.open': 'Open the tenancy',
  'rollout.wave.refused': 'The wave was not changed.',

  'rollout.state.applying': 'Moving',
  'rollout.state.waiting': 'Waiting to be signed off',
  'rollout.state.halted': 'Stopped',
  'rollout.state.done': 'Finished',
  'rollout.state.unknown': 'A state this console does not know',

  'rollout.advance.blocked.title': 'Signing off is not on offer',
  'rollout.advance.blocked.noReason':
    'The answer said the wave may not be signed off and named no reason. Nothing here will guess at one.',
  'rollout.advance.submit': 'Sign this wave off',

  'rollout.retry.blocked.title': 'Moving anything again is not on offer',
  'rollout.retry.blocked.noReason':
    'The answer said nothing may be moved again and named no reason. Nothing here will guess at one.',
  'rollout.retry.submit': 'Move those again',

  'rollout.forecast.title': 'What this move crosses',
  'rollout.forecast.body':
    'Breaks gdsgate has itself named, lying between what the fleet is seen running and the version it is being moved to.',
  'rollout.forecast.none':
    'Nothing named lies between what the fleet is seen running and this version. That is the absence of a stated break, not a promise that the move is safe.',
  'rollout.namedIn': 'Named in:',

  'rollout.wave.canary': 'Canary',
  'rollout.wave.rest': 'Rest',
  'rollout.wave.unknown': 'A wave this console does not know',
  'rollout.wave.choose': 'Wave',
  'rollout.wave.put': 'Put in this wave',
  'rollout.wave.putting': 'Putting',
  'rollout.wave.underWay':
    'Not while a rollout is going: stop it, move the tenancy, and start another.',

  'rollout.unmeasurable.title': 'What cannot be read at all',
  'rollout.unmeasurable.body':
    'Two of the four components answer nothing about their version, and never will as things stand. This is not a reading that failed — there is nothing to ask.',
  'rollout.unmeasurable.pairs': 'So these pairs are beyond measuring on every tenancy:',
  'rollout.unmeasurable.consequence':
    'A connector runs inside the customer’s own network and is upgraded by the customer, so it drifts furthest of the four — and its pair is the one nothing here can compare.',

  'rollout.component.notRead': 'Not read',
  'rollout.component.unreadable': 'Cannot be read',
  'rollout.component.noReason':
    'The answer left the version out and named no reason. Nothing here fills that in.',

  'rollout.verdict.same': 'Same build',
  'rollout.verdict.same.meaning': 'Both ends were seen running one build.',
  'rollout.verdict.noNamedBreak': 'Nothing named',
  'rollout.verdict.noNamedBreak.meaning':
    'Two different versions, and gdsgate has named no break between them touching this pair. Not a promise that they work together: gdsgate promises compatibility inside one product tag.',
  'rollout.verdict.broken': 'Named break',
  'rollout.verdict.broken.meaning':
    'A break gdsgate itself named lies between the two versions these were seen running.',
  'rollout.verdict.untold': 'Not known',
  'rollout.verdict.untold.meaning':
    'One end or both could not be read, so there is nothing to compare. The reason says which end.',
  'rollout.verdict.unknown': 'A verdict this console does not know',
  'rollout.verdict.unknown.meaning':
    'The answer carried a verdict this console has no wording for. It is shown rather than dropped.',
  'rollout.broken.title': 'A named break inside this tenancy',
  'rollout.broken.body':
    'Two of its own components were seen running versions gdsgate has named a break between. This stops the wave it is in from being signed off.',

  'rollout.tenant.declared.note':
    'What somebody typed: it says what the next start of a container will pull, not what is running now. Everything above it is what something answered.',
  'rollout.tenant.observed.never':
    'Never. These components have not been read since the control plane began recording them.',
  'rollout.tenant.observed.how':
    'The readings are taken while a tenancy is being reconciled, and there is no call that only re-reads them.',
  'rollout.tenant.notMovable':
    'No wave moves this tenancy: moving it would mean reconciling it, and that would put back what was deliberately taken away.',

  'rollout.move.title': 'What this rollout did to it',
  'rollout.move.from': 'From',
  'rollout.move.pending': 'Not moved yet',
  'rollout.move.moved': 'Moved',
  'rollout.move.movedAfterFailure': 'Moved, after a failure',
  'rollout.move.failed': 'Not moved',
  'rollout.move.unknown': 'An outcome this console does not know',
  'rollout.move.attempts': { one: '{count} attempt', other: '{count} attempts' },
  'rollout.move.failures': {
    one: '{count} of them failed',
    other: '{count} of them failed',
  },

  'rollout.tenants.none.title': 'No tenancies to show',
  'rollout.tenants.none.body': 'This account is entitled to none, or none is declared.',

  'rolloutStart.title': 'Start a rollout',
  'rolloutStart.subtitle': 'One version, wave by wave, one tenancy at a time.',
  'rolloutStart.target.label': 'Version to move the fleet to',
  'rolloutStart.target.hint':
    'The image tag every wave is moved to, exactly as it will be written into each tenancy’s compose file.',
  'rolloutStart.target.fromRegistry': 'Taken from the image registry: the versions it holds, newest first.',
  'rolloutStart.target.choose': 'Choose a version…',
  'rolloutStart.target.floating':
    'A floating tag is accepted and names a different build tomorrow. The board then has no forecast, and says so.',
  'rolloutStart.check.required': 'Enter the version to move to.',
  'rolloutStart.willMove.title': 'The {wave} wave moves now',
  'rolloutStart.willMove.order':
    'These tenancies, one at a time, in this order. Each is pointed at the version and then reconciled, which restarts its containers.',
  'rolloutStart.willMove.none':
    'This wave holds nobody, and a rollout that starts with an empty canary is refused: the whole fleet would move on the strength of a wave that showed nothing.',
  'rolloutStart.then.title': 'And then nothing, until you say so',
  'rolloutStart.then.body':
    'When this wave has been dealt with it waits. The waves after it do not start until somebody signs the one before off.',
  'rolloutStart.forecastLater':
    'What this move crosses appears on the board as soon as the rollout exists.',
  'rolloutStart.submit': 'Start it',
  'rolloutStart.submitting': 'Starting',
  'rolloutStart.refused.title': 'The rollout was not started',
  'rolloutStart.refused.body':
    'Nothing has moved. A rollout is already under way, or the first wave holds no tenancy a reconcile may run against.',

  'rolloutSignOff.title': 'Sign this wave off',
  'rolloutSignOff.subtitle': 'The {wave} wave, on {target}.',
  'rolloutSignOff.already.title': 'What has happened in this wave',
  'rolloutSignOff.already.moved': 'Moved and accepted:',
  'rolloutSignOff.already.repaired': 'Moved, but only after a failure:',
  'rolloutSignOff.already.failed': 'Not moved, and the move said why:',
  'rolloutSignOff.already.pending': 'Not dealt with yet:',
  'rolloutSignOff.already.nobody': 'Nobody.',
  'rolloutSignOff.next.title': 'What signing off will move: the {wave} wave',
  'rolloutSignOff.next.order':
    'These tenancies go to {target} next, one at a time, in this order — as soon as this is signed off.',
  'rolloutSignOff.next.none': 'That wave holds nobody, so signing off moves nothing.',
  'rolloutSignOff.finishes.title': 'This finishes the rollout',
  'rolloutSignOff.finishes.body':
    'There is no wave after this one, so nothing further moves. The fleet stays on what it has been moved to.',
  'rolloutSignOff.judgement.title': 'This is your judgement, not the machine’s',
  'rolloutSignOff.judgement.body':
    'The control plane refuses a sign-off while it can see something wrong. That the list is empty lifts the veto and says nothing else: the thing a canary is kept for is a customer ringing up, and nothing here can see it.',
  'rolloutSignOff.judgement.soak':
    'It also insists the wave has stood at the new version for the time this deployment asks for.',
  'rolloutSignOff.submit': 'Sign off and go on',
  'rolloutSignOff.submitting': 'Signing off',
  'rolloutSignOff.refused.title': 'The wave was not signed off',
  'rolloutSignOff.refused.body':
    'Nothing further has moved. When the reason is the wave itself, the sentence above names the tenancies to go and look at.',

  'rolloutRetry.title': 'Move those again',
  'rolloutRetry.subtitle': 'The {wave} wave, on {target}.',
  'rolloutRetry.willMove.title': 'These move again',
  'rolloutRetry.willMove.body':
    'The tenancies of this wave that did not move, one at a time, in this order. Each is pointed at {target} and reconciled, exactly as the first attempt did.',
  'rolloutRetry.notAWayPast.title': 'This is not a way past the wave',
  'rolloutRetry.notAWayPast.body':
    'Only the tenancies that did not move are touched. The wave still has to be signed off afterwards, and the wave after it still waits for that sign-off. Which tenancies move follows from the steps, not from anything chosen here.',
  'rolloutRetry.notAWayPast.record':
    'The repair does not erase that it was needed: each tenancy keeps its count of attempts and failures.',
  'rolloutRetry.submit': 'Move them again',
  'rolloutRetry.submitting': 'Moving',
  'rolloutRetry.refused.title': 'Nothing was moved again',
  'rolloutRetry.refused.body':
    'Nothing has moved. When the reason is that every tenancy of this wave moved, what is still holding the wave is about a tenancy rather than about a move, and it is put right on that tenancy’s own card.',

  'rolloutStop.title': 'Stop the rollout',
  'rolloutStop.subtitle': 'On {target}, in the {wave} wave.',
  'rolloutStop.effect.title': 'What stopping does, and what it does not',
  'rolloutStop.effect.moved':
    'What has moved stays where it is. This is not an undo, and there is none: putting a fleet back is another rollout, aimed at the version it came from.',
  'rolloutStop.effect.back':
    'The tenancies that have not moved stay on what they were on, and no further one is touched.',
  'rolloutStop.effect.next':
    'A move already under way finishes; the stop is obeyed at the next tenancy rather than in the middle of one.',
  'rolloutStop.effect.ends':
    'Stopping ends this rollout. It stays on the board as the last one, and holds nothing up: the next rollout can be started straight away.',
  'rolloutStop.submit': 'Stop it',
  'rolloutStop.submitting': 'Stopping',

  // ---------- declaring a tenancy ----------
  'createTenant.title': 'Declare a tenancy',
  'createTenant.subtitle': 'The record is written now; the cluster is built by the next application.',
  'createTenant.name.label': 'Name',
  'createTenant.name.placeholder': 'Acme Corp',
  'createTenant.slug.label': 'Slug',
  'createTenant.slug.hint':
    'One DNS label: lower-case letters, digits, hyphens inside. It becomes the cluster profile and the name of its directory, and it does not change afterwards.',
  'createTenant.slug.placeholder': 'acme',
  'createTenant.names.title': 'The names follow from the slug',
  'createTenant.names.body':
    'The cluster console answers on <slug>-app and the gateway on <slug>-proxy, both in the zone this deployment names tenancies in. There is no box for them: the zone is not a customer’s to choose, and the two names are what the tenancy’s certificate is asked for.',
  'createTenant.version.label': 'gdsgate version',
  'createTenant.version.hint':
    'The image tag this tenancy runs. Pinned per tenancy, which is what makes a staged rollout possible.',
  'createTenant.version.placeholder': 'v0.7.0',
  'createTenant.adminGroup.label': 'Administrator group',
  'createTenant.adminGroup.hint':
    'The value in the groups claim that makes a person an administrator of this cluster. It cannot be changed afterwards. A name beginning with {namespace} is refused: that space belongs to the identities a cluster issues itself.',
  'createTenant.adminGroup.placeholder': 'acme-admins',
  'createTenant.adminGroup.slugTaken.title': 'This slug cannot use the usual name',
  'createTenant.adminGroup.slugTaken.body':
    'The name this tenancy would otherwise be given, {name}, falls inside {namespace} — the space a cluster keeps for the identities it issues itself. Choose a name that does not begin with {namespace}.',
  'createTenant.identity.title': 'No identity provider is chosen here',
  'createTenant.identity.body':
    'The cluster starts on the provider’s own identity provider, so that it is reachable before its owner has been asked anything. The tenancy chooses for itself afterwards.',
  'createTenant.provider.section': 'The customer’s identity provider',
  'createTenant.secrets.title': 'No secrets are entered here',
  'createTenant.secrets.body':
    'Database credentials and signing keys are placed by the provisioner and never travel through the console.',
  'createTenant.submit': 'Declare',
  'createTenant.submitting': 'Declaring',
  'createTenant.check.nameRequired': 'Enter a name.',
  'createTenant.check.slugRequired': 'Enter a slug.',
  'createTenant.check.slugFormat': 'Use lower-case letters, digits and hyphens.',
  'createTenant.check.versionRequired': 'Enter a version.',
  'createTenant.check.adminGroupRequired': 'Enter a group name.',
  'createTenant.check.adminGroupReserved':
    '{namespace} belongs to the identities a cluster issues itself, and a provider that emits such a group has its whole token refused. Choose a name that does not begin with it.',
  'createTenant.check.adminGroupReservedTry':
    '{namespace} belongs to the identities a cluster issues itself, and a provider that emits such a group has its whole token refused. Try {name}.',
  'createTenant.taken.title': 'The slug or the domain is taken',
  'createTenant.taken.body':
    'One of the two is already in use. The answer does not say which — uniqueness is checked by storage — so both are worth looking at.',
  'createTenant.noProvider.title': 'This deployment runs no identity provider of its own',
  'createTenant.noProvider.body':
    'A cluster cannot be stood up on a provider that does not exist, so the customer’s own has to be named here. The fields for it are below.',

  // ---------- identity provider ----------
  'identityProvider.title': 'Identity provider',
  // ---------- the address a provider returns to ----------
  'redirectUri.title': 'Register this return address at your provider',
  'redirectUri.body':
    'Your identity provider sends the browser back here after a sign-in. Register it in the client you use for this cluster — Keycloak calls it Valid redirect URIs — and it is compared byte for byte.',
  'redirectUri.label': 'Return address',
  'redirectUri.stable':
    'It follows from this tenancy’s domain and does not change when the identity provider does: register the same string with the new one.',
  'redirectUri.unnamed':
    'The answer carried no return address, so there is nothing to copy. A guessed one is an address that fails at the provider.',

  'identityProvider.domains.note':
    'The cluster console is on the tenancy domain; the gateway your clients and connectors dial is a separate host in the same domain.',
  'identityProvider.openConsole': 'Open the cluster console',

  'identityProvider.inUse.title': 'What the cluster runs',
  'identityProvider.none':
    'Nothing has been applied. The cluster is up and its console lets nobody in until something is.',
  'identityProvider.declared.title': 'What has been asked for',
  'identityProvider.declared.note':
    'Declared and not running. The cluster keeps what it has until a change is applied.',
  'identityProvider.adminGroup.note':
    'Whichever provider is chosen has to put the administrator group in the groups claim, or people will sign in and be refused everything.',

  'identityProvider.kind.provider': 'The provider’s identity provider',
  'identityProvider.kind.provider.body':
    'Sign-in runs through {issuer}, which the provider operates. Nothing to set up and nothing to run.',
  'identityProvider.kind.own': 'Your own identity provider',
  'identityProvider.kind.own.body':
    'Any OIDC provider you already run — the cluster verifies tokens against its published signing keys.',
  'identityProvider.provider.unavailable':
    'This deployment runs no identity provider of its own, so a cluster cannot be pointed at one. Name your own below.',
  'identityProvider.provider.unavailable.title': 'That option is not available here',
  'identityProvider.provider.unnamed':
    'This deployment reports an identity provider of its own but does not say where it is, so there is nothing to describe.',
  'identityProvider.issuer.unnamed': 'The answer named no address for it.',

  'identityProvider.price.title': 'What this costs you',
  'identityProvider.price.provider':
    'Enrolment and revocation of your people move to the provider: you ask us, and we do it. Less to run for a team without a directory of its own; control handed over for a team with one.',
  'identityProvider.price.own':
    'Your people stay yours: revoking someone at your provider closes this cluster’s console and its hosts and databases in one action. The other side of it: a mistake in these fields locks everyone out, and the way back is this screen.',

  'identityProvider.change': 'Change the provider',
  'identityProvider.running': 'Sign-in runs through',
  'identityProvider.choose.legend': 'Who issues the tokens the cluster trusts',
  'identityProvider.own.section': 'Provider settings',
  'identityProvider.field.kind': 'Kind',
  'identityProvider.field.issuer': 'Issuer',
  'identityProvider.field.clientId': 'Client ID',
  'identityProvider.field.audience': 'Audience',
  'identityProvider.field.signingAlgs': 'Token signing algorithms',
  'identityProvider.issuer.hint':
    'The base URL. Discovery is fetched from it, and the iss claim of every token has to equal it byte for byte. No query, no fragment, no trailing slash.',
  'identityProvider.issuer.placeholder': 'https://login.acme.example',
  'identityProvider.clientId.hint': 'The OAuth client registered for gdsgate at your provider.',
  'identityProvider.clientId.placeholder': 'gdsgate',
  'identityProvider.audience.hint': 'The expected aud claim. Leave empty to use the client ID.',
  'identityProvider.audience.default': 'The client ID',
  'identityProvider.signingAlgs.hint':
    'Leave everything unticked to accept every asymmetric algorithm. Symmetric ones are never accepted: the key that verifies such a token also mints one.',
  'identityProvider.signingAlgs.all': 'Every asymmetric algorithm',
  'identityProvider.secrets.title': 'No client secret is entered here',
  'identityProvider.secrets.body':
    'gdsgate verifies tokens against your published signing keys, and sign-in runs against a public client. A provider that will only issue to a confidential client cannot be used.',

  'identityProvider.effect.title': 'What applying does',
  'identityProvider.effect.restart':
    'It reconfigures the cluster. Auth reads its identity-provider settings once, at start, so applying a choice restarts it.',
  'identityProvider.effect.pause':
    'While that runs, no sign-in and no new access is issued. It is seconds, not minutes.',
  'identityProvider.effect.sessions': 'Sessions already open keep running.',
  'identityProvider.submit': 'Apply this choice',
  'identityProvider.startedAt': 'Started {when}.',

  'identityProvider.check.issuerRequired': 'Enter an issuer URL.',
  'identityProvider.check.issuerScheme':
    'The issuer has to begin with https://. A plaintext discovery or key fetch is rewritable by anyone on the path.',
  'identityProvider.check.issuerNoHost': 'This names no host.',
  'identityProvider.check.issuerQuery': 'An issuer is a base URL: no query and no fragment.',
  'identityProvider.check.issuerTrailingSlash':
    'Drop the trailing slash. It is compared byte for byte with what the provider advertises.',
  'identityProvider.check.issuerWhitespace': 'This carries whitespace.',
  'identityProvider.check.clientIdRequired': 'Enter a client ID.',
  'identityProvider.check.clientIdWhitespace': 'A client ID carries no whitespace.',
  'identityProvider.check.audienceWhitespace':
    'An audience carries no whitespace. Leave it empty to accept the client ID.',

  // ---------- the state of a change ----------
  'identityState.pending': 'Declared, not applied',
  'identityState.applying': 'Applying',
  'identityState.applied': 'Applied',
  'identityState.rejected': 'Refused before anything was touched',
  'identityState.failed': 'Applied and did not land',
  'identityState.unknown': 'A state this console does not know',
  'identityState.pending.badge': 'Declared',
  'identityState.applying.badge': 'Applying',
  'identityState.applied.badge': 'Applied',
  'identityState.rejected.badge': 'Refused',
  'identityState.failed.badge': 'Did not land',
  'identityState.unknown.badge': 'Unknown',

  'identityProvider.applying.body':
    'The provider is being asked, and if it answers, Auth is restarted into it. This page follows it on its own.',
  'identityProvider.applying.sessions':
    'No sign-in is issued until it finishes. Sessions already open are not touched.',
  'identityProvider.rejected.body':
    'Nothing was touched: the files are as they were and Auth was not restarted. Put the settings right at your provider’s end and apply again.',
  'identityProvider.failed.body':
    'The check passed and applying did not finish. The previous configuration was written back, so nobody who could sign in has lost access.',
  'identityProvider.applyAgain': 'Apply the same choice again',
  'identityProvider.dismiss': 'Drop this attempt',
  'identityProvider.revert': 'Go back to what ran before',
  'identityProvider.noPrevious':
    'There is nothing to go back to: this cluster has never had another provider applied.',
  'identityProvider.underWay.title': 'A change is already running',
  'identityProvider.underWay.body':
    'One change of provider is already under way for this tenancy. A second is refused rather than queued: each one restarts the tenancy’s control plane.',
  'identityProvider.nothingToDismiss.title': 'There is nothing to drop',
  'identityProvider.nothingToDismiss.body':
    'The last change did not fail, so there is no report to clear. The screen has been read again.',
  'identityProvider.nowhereBack.title': 'There is nowhere to go back to',
  'identityProvider.nowhereBack.body':
    'No identity provider was ever applied to this cluster, so there is nothing that is known to have worked.',
  'identityProvider.refused.title': 'The choice was refused',
  'identityProvider.refused.body':
    'It did not pass the checks made before the provider is asked anything. The fields at fault are named below.',
  'identityProvider.notSole.title': 'This account does not represent one tenancy',
  'identityProvider.notSole.body':
    'The choice follows from the account, and this one is bound to no tenancy or to several.',
  'identityProvider.watch.gaveUp.title': 'This has been running a long time',
  'identityProvider.watch.gaveUp.body':
    'The change has not settled in longer than one can hold this tenancy’s lock. The server frees a stale lock by the clock; reading again is safe.',

  // ---------- why a change did not take ----------
  'identityFailure.issuerUnreachable':
    'The issuer did not answer: its discovery document could not be fetched.',
  'identityFailure.issuerCertificate':
    'The provider answered, and the certificate it presented did not verify — expired, signed by an authority this deployment does not trust, or not for that name. It is up and answering everybody else; what has to be dealt with is the certificate. The line below carries the date it ran out on.',
  'identityFailure.issuerMismatch':
    'The provider advertises an issuer other than the one entered. They have to be byte-identical — the commonest cause is a console address pasted in place of the issuer.',
  'identityFailure.algorithmUnsupported':
    'The provider signs tokens with none of the chosen algorithms.',
  'identityFailure.signingKeysUnusable':
    'The provider’s key set holds nothing a token could be verified with.',
  'identityFailure.noLoginFlow':
    'The provider advertises neither a device flow nor an authorization endpoint, so there is no way for a person to sign in through it.',
  'identityFailure.clientUnknown':
    'The provider does not know the client that was named. Register it there first.',
  'identityFailure.clusterUnreachable':
    'The check passed and the cluster did not come back on the new provider.',
  'identityFailure.unknown':
    'It did not go through, and the answer gives no code this console knows. The line below is the server’s own.',

  // ---------- confirming a change ----------
  'switchProvider.title': 'Change how people sign in?',
  'switchProvider.subtitle': 'Confirming starts the change; it does not wait for the result.',
  'switchProvider.target': 'What will be applied',
  'switchProvider.wayBack.title': 'If it does not work',
  'switchProvider.wayBack.body':
    'The cluster stays on what it runs now unless the new provider answers, and this screen — in the account, not in the cluster console — is where it is undone.',
  'switchProvider.wayBack.first':
    'Nothing has been applied yet, so there is nothing to fall back to. If the provider does not answer, correct the settings here and apply again.',
  'switchProvider.submit': 'Apply',
  'switchProvider.submitting': 'Starting',

  // ---------- applying the desired state ----------
  'reconcile.section': 'The last application',
  'reconcile.none': 'Nothing has been applied to this tenancy yet.',
  'reconcile.submit': 'Apply the declared state',
  'reconcile.running': 'Applying',
  'reconcile.done': 'The application finished: {outcome}',
  'reconcile.field.actor': 'Asked for by',
  'reconcile.field.finished': 'Finished',
  'reconcile.col.step': 'Step',
  'reconcile.col.outcome': 'Outcome',
  'reconcile.col.detail': 'Detail',
  'reconcile.outcome.accepted': 'Accepted',
  'reconcile.outcome.notAccepted': 'Not accepted',
  'reconcile.outcome.failed': 'Failed',
  'reconcile.outcome.unknown': 'Unknown',
  'reconcile.step.unchanged': 'Unchanged',
  'reconcile.step.applied': 'Applied',
  'reconcile.step.skipped': 'Skipped',
  'reconcile.step.beforeContract': 'outside the contract',
  'reconcile.step.beforeContract.hint':
    'The tenancy’s certificate has to be on the disk before the configuration that names it is rendered, and gdsgate’s provisioning contract has no step for something that happens before all of it.',

  // ---------- the tenancy's certificate ----------
  'certificate.section': 'Certificate',
  'certificate.fromLast': 'Read on the last application',
  'certificate.absent.noRun':
    'Nothing has been applied to this tenancy yet, so nothing has looked at its certificate.',
  'certificate.absent.noBlock':
    'The last application says nothing about a certificate: it ran before the control plane had a certificate step. What is on the disk is not known here, and this console will not guess at it.',

  'certificate.outcome.kept': 'Kept',
  'certificate.outcome.kept.meaning':
    'The authority was not asked: the pair on the disk is valid and covers every name, so it was reused. That is the wanted outcome — the authority counts its limits against the provider’s registered domain, shared by every tenancy.',
  'certificate.outcome.obtained': 'Obtained',
  'certificate.outcome.obtained.meaning':
    'This application obtained a pair and installed it. Both listeners were pointed at it.',
  'certificate.outcome.waiting': 'Waiting to try again',
  'certificate.outcome.waiting.meaning':
    'An attempt failed and the wait after it has not run out, so the authority is not asked again yet. A renewal begins a third of a certificate’s lifetime before it expires, which leaves about thirty attempts.',
  'certificate.outcome.failed': 'Not obtained',
  'certificate.outcome.failed.meaning':
    'This application asked and did not get one. The line above is the reason the authority or the challenge gave.',
  'certificate.outcome.foreign': 'Installed by somebody else',
  'certificate.outcome.foreign.meaning':
    'The pair on the disk was not put there by the control plane, so it is left exactly as it is. Renewal belongs to whoever installed it; removing the pair hands the tenancy over.',
  'certificate.outcome.untended': 'None obtained here',
  'certificate.outcome.untended.meaning':
    'The control plane obtains no certificate for this tenancy: either no authority is configured, or these names are in a zone this deployment cannot write a record in. A pair installed by hand is picked up and left alone.',
  'certificate.outcome.unknown': 'An outcome this console does not know',
  'certificate.outcome.unknown.meaning':
    'The answer names an outcome this console has never heard of. The line above is the server’s own.',

  'certificate.names.section': 'The names',
  'certificate.names.why':
    'Both sets, because only the pair of them can say the thing nothing else notices: a name this tenancy is published under that the certificate does not carry. There is no symptom on this side.',
  'certificate.names.wanted': 'Published under',
  'certificate.names.installed': 'The certificate carries',
  'certificate.names.installed.none':
    'There is no certificate on the disk, so there are no names to read off one.',
  'certificate.names.agree': 'The two sets are the same.',
  'certificate.names.agree.badge': 'The sets agree',
  'certificate.names.diverged.title': 'The two sets do not agree',
  'certificate.names.missing':
    'Published and not covered. This fails the certificate step, so the tenancy is not handed over.',

  'certificate.trust.section': 'What a client makes of it',
  'certificate.trust.trusted': 'Publicly trusted',
  'certificate.trust.trusted.meaning': 'The authority is one a client already trusts.',
  'certificate.trust.test': 'From the test authority',
  'certificate.trust.test.meaning':
    'Not an error: the test authority answers exactly this way. The TLS is real — but a client or a connector that was not told about that root refuses the gateway, so this is not a certificate to hand a customer.',
  'certificate.trust.notSaid': 'Not said',
  'certificate.trust.notSaid.meaning':
    'The control plane did not install this pair, so it makes no claim about the authority that did.',
  'certificate.authority': 'Obtained from',
  'certificate.authority.none':
    'The report names no authority: this control plane obtained nothing here.',

  'certificate.valid.notAfter': 'Valid until',
  'certificate.valid.renewAt': 'Renewed from',
  'certificate.valid.none':
    'There is no certificate on the disk, so there is no date to read off one.',
  'certificate.valid.noDate': 'The report carries no date for this.',
  'certificate.valid.hint':
    'The renewal threshold is worked out from the certificate’s own lifetime — a third of it left — and not from a fixed number of days.',

  'certificate.attempts.waiting.title': 'Waiting for the next attempt',
  'certificate.attempts.waiting.body':
    'Attempts that failed in a row: {attempts}. The next is not made before {when}. The wait starts at fifteen minutes and doubles to a day at most, so a failure that repeats on a schedule is a wait rather than a breakage.',
  'certificate.attempts.stalled.title': 'It failed, and no next attempt is named',
  'certificate.attempts.stalled.body':
    'Attempts that failed in a row: {attempts}. The report names no time for another one, so nothing here says another is coming. The line above is the reason the last one gave.',

  'certificate.relay.title': 'No registration relay for this tenancy',
  'certificate.relay.body':
    'A tenancy with no certificate publishes no registration relay, so a connector at the customer’s own site has nowhere to spend its join token. The address is withheld rather than unknown: the registration listener answers in cleartext.',
  'certificate.relay.nearby':
    'A node inside the tenancy’s own network is unaffected: it registers straight to Auth there.',

  // ---------- the anonymous surface of the gateway ----------
  'budget.section': 'Anonymous surface',
  'budget.absent.noRun':
    'Nothing has been applied to this tenancy yet, so no budget has been written for it.',
  'budget.absent.noBlock':
    'The last application says nothing about a budget: it ran before the control plane wrote these keys.',
  'budget.what':
    'Two calls on the gateway’s client-facing listener answer before there is anybody to recognise. The budget is charged per call and not per connection.',
  'budget.rate': 'Calls a second, per source address',
  'budget.burst': 'Burst',
  'budget.burst.hint':
    'What a momentarily idle address may send back to back. A whole office behind one address shares the bucket, so the burst is generous and the rate is not.',
  'budget.notLimited':
    'A call made by somebody the cluster has already recognised is not charged to it at all.',
  'budget.state.inForce.meaning':
    'The gateway started after these numbers were written, so the process running now read them and is holding anonymous callers to them.',
  'budget.state.notReadYet.meaning':
    'The gateway running now is the very process that was running when these numbers were written, so it cannot have read them. gdsgate reads `[proxy]` once, when it starts — the line above names what it is holding instead, until its next start.',
  'budget.state.notReadYet.noRestart':
    'Nothing here restarts it to close that window: recreating a gateway drops every live session and every connector tunnel.',
  'budget.state.atTheNextStart.meaning':
    'Nothing is running this tenancy’s gateway, so nobody is being held to anything. Whatever starts next reads these numbers.',
  'budget.state.notKnown.meaning':
    'Whether the gateway running now has read these numbers cannot be answered: either the container runtime could not be asked what is running, or there is no witness of when the numbers were written. The line above says which.',
  'budget.state.notKnown.notAFinding':
    'This is not a finding: nothing here says the gateway is outside the budget, only that this pass cannot say it is inside it. The gateway’s next start settles it.',
  'budget.state.notWritten.meaning':
    'The configuration step did not run on this pass, so nothing was written and nothing was read. This report is not the one to read a budget off.',
  'budget.state.unknown.meaning':
    'The answer names a state this console has never heard of. The line above is the server’s own.',
  'budget.aboutTheProcess':
    'This says what the gateway’s process is holding callers to, not what its file carries. The control plane compares what was running when each number was written with what is running now.',
  'budget.refusals':
    'How often the budget actually refuses anybody is not shown here: the gateway counts refusals on a telemetry listener published only inside the tenancy’s network, which nothing in the control plane collects.',

  // ---------- what each role has read of what was rendered for it ----------
  'settings.state.inForce': 'Read by the process running it',
  'settings.state.notReadYet': 'Written, and proved unread',
  'settings.state.atTheNextStart': 'Waiting: the role is not running',
  'settings.state.notKnown': 'Cannot be answered',
  'settings.state.notWritten': 'Nothing written on this application',
  'settings.state.unknown': 'A state this console does not know',
  'settings.section': 'What each role has read',
  'settings.absent.noRun':
    'Nothing has been applied to this tenancy yet, so nothing has been rendered for its roles.',
  'settings.absent.noBlock':
    'This application says nothing about what the roles have read: it ran before the control plane began comparing. What those roles are holding is not known from this report.',
  'settings.what':
    'A role reads its configuration once, when it starts, so every value rendered for it has a window between being written and being in effect. This is where that stands, role by role.',
  'settings.role.authority': 'Auth, the tenancy’s control plane',
  'settings.role.proxy': 'The gateway',
  'settings.unread': 'Waiting for the next start',
  'settings.unread.none':
    'Nothing of this role’s is waiting for a start: the state beside it is the whole answer.',
  'settings.namesOnly':
    'Named and not printed. A value shown on a card is a value shown to everybody who may read the card, so nothing here prints one. The spelling is gdsgate’s own.',
  'settings.noRestart':
    'Nothing here restarts a role to close the window: recreating one drops every live session and every connector tunnel.',
  'settings.outside':
    'Two things are deliberately not in this list, because neither waits for a start: the certificate pair, which gdsgate looks at again as connections arrive, and the policy document, which the tenancy’s own cluster reloads.',

  // ---------- what this control plane did ----------
  'trail.section': 'Trail',
  'trail.col.at': 'When',
  'trail.col.actor': 'Who',
  'trail.col.action': 'Action',
  'trail.col.outcome': 'Outcome',
  'trail.col.detail': 'Particulars',
  'trail.count': { one: '{count} line, newest first', other: '{count} lines, newest first' },
  'trail.capped': 'The last 100, newest first',
  'trail.none.title': 'Nothing is recorded for this tenancy',
  'trail.none.body': 'Every change this control plane makes to it lands here.',

  // ---------- catalogue (cluster console) ----------
  'catalog.title': 'Catalogue',
  'catalog.declared.title': 'This is the declared catalogue',
  'catalog.declared.body':
    'It is what the control plane will make true on the next application, not what the cluster holds at this second.',
  'catalog.writtenHere.body':
    'This is where it is written, and the only place. A change carries your name to this tenancy’s own audit trail, marked there as claimed by the caller and not verified.',
  'catalog.readOnly.body':
    'Writing is for the administrators this tenancy’s policy names, and this session is not one of them.',
  'catalog.declare': 'Declare a resource',
  'catalog.edit': 'Edit',
  'catalog.remove': 'Remove',
  'catalog.notTold.title': 'Declared, and the cluster was not told',
  'catalog.notTold.written':
    '{id} is in the declared catalogue. The cluster could not be reached, so it does not have it yet.',
  'catalog.notTold.removed':
    '{id} is out of the declared catalogue. The cluster could not be reached, so it is still serving it.',
  'catalog.notTold.next':
    'Nothing needs typing again. It arrives with the next application of this tenancy’s declared state, which an operator can run from the account.',
  'catalog.notTold.unnamed':
    'That application will not carry your name: by then nobody is standing in front of it.',
  'catalog.search.label': 'Search resources',
  'catalog.search.placeholder': 'Name, project, label',
  'catalog.filter.label': 'Filter by kind',
  'catalog.filter.all': 'All',
  'catalog.col.id': 'Resource',
  'catalog.col.connector': 'Connector',
  'catalog.col.served': 'Status',
  'catalog.served.yes': 'Online',
  'catalog.served.unknown': '—',
  'catalog.served.unknown.hint':
    'Nothing has been heard about this one. A connector says what it serves when its tunnel opens, from memory, so a backend added since — or a connector that has not restarted — serves perfectly and reports nothing. This cluster publishes no liveness signal to fall back on.',
  'catalog.served.no': 'Offline',
  'catalog.served.no.hint':
    'No connector has offered this id. Declared and unserved is a resource clients are told about and cannot reach.',
  'common.yes': 'Yes',
  'common.no': 'No',
  'catalog.col.kind': 'Kind',
  'catalog.col.address': 'Address',
  'catalog.col.project': 'Project',
  'catalog.col.environment': 'Environment',
  'catalog.col.labels': 'Labels',
  'catalog.col.lease': 'Lease',
  'catalog.lease.expired': 'expired',
  'catalog.lease.secs': '{secs}s left',
  'catalog.lease.mins': '{mins}m left',
  'catalog.lease.hours': '{hours}h left',
  'scopes.title': 'Boundaries',
  'scopes.count': { one: '{count} boundary', other: '{count} boundaries' },
  'scopes.none.body':
    'A boundary lets one connector write catalogue rows itself, inside a prefix you set. Use it where targets come and go faster than anyone can declare them.',
  'scopes.new.title': 'Declare a boundary',
  'scopes.edit.title': 'The boundary',
  'scopes.subtitle':
    'Inside it the named connector writes catalogue rows itself. Each row holds a lease and goes when nothing renews it.',
  'scopes.submit': 'Declare',
  'scopes.submitting': 'Declaring',
  'scopes.field.prefix': 'Prefix',
  'scopes.prefix.hint': 'Every id written inside the boundary starts with it. `k8s-`, `test-`.',
  'scopes.prefix.fixed': 'Fixed once the boundary exists: the rows are written under it.',
  'scopes.prefix.placeholder': 'k8s-',
  'scopes.field.node': 'Connector',
  'scopes.node.unset': 'Choose a connector',
  'scopes.node.unreadable': 'The registry could not be read; type the connector id.',
  'scopes.field.kinds': 'Kinds',
  'scopes.kinds.hint':
    'What a target may be published as. A snapshot carrying any other kind is refused whole, so name every kind this connector finds.',
  'scopes.kinds.placeholder': 'type to narrow',
  'scopes.kinds.remove': 'Remove kind',
  'scopes.node.hint': 'The registry id of the connector allowed to publish here. Nodes are listed on the nodes screen.',
  'scopes.field.kind': 'Kind',
  'scopes.kind.hint': 'What a target may be published as. A snapshot carrying any other kind is refused whole.',
  'scopes.field.project': 'Project',
  'scopes.project.hint': 'Written on every row here.',
  'scopes.field.environment': 'Environment',
  'scopes.environment.placeholder': 'prod, staging, test',
  'scopes.check.prefixRequired': 'A boundary needs a prefix: every id inside it starts with one.',
  'scopes.check.prefixShape': 'Letters, digits, `-`, `_` or `.` — what a resource id may carry.',
  'scopes.check.nodeRequired': 'Choose the connector that fills this boundary.',
  'scopes.check.kindsRequired': 'Name at least one kind, or every snapshot is refused.',
  'scopes.check.environmentRequired': 'A boundary states the environment for every row it holds.',
  'scopes.check.noWhitespace': 'No spaces.',
  'scopes.check.maxLiveRange': 'A whole number of rows, one or more.',
  'scopes.check.leaseRange': 'A whole number of seconds, one or more.',
  'scopes.environment.hint': 'Written on every row here, and never taken from the source.',
  'scopes.field.maxLive': 'Row ceiling',
  'scopes.maxLive.hint':
    'How many rows the boundary holds at once, counting rows from earlier snapshots whose leases have not run out.',
  'scopes.field.leaseSecs': 'Lease, seconds',
  'scopes.leaseSecs.hint': 'How long a row stays after the snapshot that carried it.',
  'scopes.field.fallback': 'A target the connector has not published',
  'scopes.fallback.hint':
    'A probe publishes what it finds every pass, so a target created between two passes has no catalogue row yet. This says whether it can be reached meanwhile.',
  'scopes.fallback.parent': 'Reachable through its parent resource, as before boundaries existed',
  'scopes.fallback.deny': 'Not reachable until the next pass publishes it',
  'scopes.field.hostKey': 'Host key of a discovered ssh target',
  'scopes.hostKey.hint':
    'A client verifies the host it lands on against this fingerprint, so it decides what a session is protected against.',
  'scopes.hostKey.annotation':
    'Taken from the object itself (gdsgate.io/ssh-host-key); a target that states none is not served',
  'scopes.hostKey.tofu':
    'The first key seen is trusted for the life of the row, and a change after that is refused',
  'scopes.blurb':
    'Inside a boundary the named connector writes catalogue rows itself. Each row holds a lease and disappears when nothing renews it.',
  'scopes.none': 'No boundary is declared.',
  'scopes.add': 'Declare a boundary',
  'scopes.col.prefix': 'Prefix',
  'scopes.col.node': 'Connector',
  'scopes.col.kinds': 'Kinds',
  'scopes.col.environment': 'Environment',
  'scopes.col.rows': 'Rows',
  'scopes.col.lease': 'Lease',
  'scopes.leaseSecs': '{secs}s',
  'scopes.disabled': 'disabled',
  'scopes.remove': 'Remove',
  'scopes.removed': 'The boundary and the rows it held are gone.',
  'scopes.declared': 'The boundary is declared.',
  'scopes.form.prefix': 'Prefix',
  'scopes.form.node': 'Connector id',
  'scopes.form.kinds': 'Kinds, comma separated',
  'scopes.form.project': 'Project',
  'scopes.form.environment': 'Environment',
  'scopes.form.maxLive': 'Row ceiling',
  'scopes.form.leaseSecs': 'Lease, seconds',
  'scopes.form.submit': 'Declare',
  'scopes.form.cancel': 'Cancel',
  'catalog.col.approvers': 'Approvers',
  'catalog.under': 'under {parent}',
  'catalog.approvers.inherited': 'inherited',
  'catalog.empty.title': 'No resources match',
  'catalog.empty.body': 'Change the filter or the search text.',
  'catalog.none.title': 'The catalogue is empty',
  'catalog.none.body': 'Nothing is served until a resource is declared for this tenancy.',
  'catalog.none.declare':
    'Nothing is served until a resource is declared. Declaring one is the first thing this console is for.',
  'catalog.count': { one: '{count} resource', other: '{count} resources' },
  'catalog.noLabels': '—',

  // ---------- one resource ----------
  'resource.new.title': 'Declare a resource',
  'resource.edit.title': 'Rewrite this resource',
  'resource.subtitle':
    'Declared first, then carried to the cluster. Every field is sent: a write replaces the resource rather than merging into it.',
  'resource.submit': 'Declare',
  'resource.submitting': 'Declaring',
  'resource.field.id': 'Name',
  'resource.field.kind': 'Kind',
  'resource.field.project': 'Project',
  'resource.field.environment': 'Environment',
  'resource.field.hostname': 'Host name',
  'resource.field.port': 'Port',
  'resource.field.aliases': 'Aliases',
  'resource.field.parent': 'Parent',
  'resource.field.approvers': 'Approvers',
  'resource.field.labels': 'Labels',
  'resource.id.hint':
    'What clients ask for and what policy names. It is the address this write goes to, and it cannot be changed afterwards.',
  'resource.id.fixed':
    'Fixed. A different name is a different resource: declaring one and removing the other, and removing closes access.',
  'resource.id.placeholder': 'orders-db',
  'resource.kind.hint': 'What is behind it. It decides how the gateway speaks to it.',
  'resource.project.hint': 'A grouping policy can be written against. Empty is not allowed; the server’s own default is default.',
  'resource.environment.hint': 'The other grouping policy is usually written against. The server’s own default is dev.',
  'resource.hostname.hint':
    'The name clients reach it by — `gdsgate up` publishes it in the `.gds` zone. Not the backend’s address: that is set in the connector and is never published. Leave empty to use the id above.',
  'resource.port.hint':
    'The port clients dial locally, not the port the backend listens on. Leave empty to use the well-known port of this kind.',
  'resource.port.required':
    'Required for this kind: TCP, MCP, a model service and a way out have no well-known port. A way out is written into $HTTP_PROXY with its port and is never dialled by name.',
  'resource.aliases.hint': 'Other names clients may use, separated by spaces.',
  'resource.parent.hint': 'For a database on a host: the resource that hosts it.',
  'resource.approvers.hint':
    'How many approvals a request for this resource needs. Leave empty to use whatever the policy asks for in general.',
  'resource.labels.hint':
    'One key=value to a line. No commas and no double spaces: the text form the catalogue is written in does not escape either.',
  'resource.field.nativeName': 'Its own domain',
  'resource.nativeName.hint':
    'The domain the application itself believes it answers on, as its connector’s backend url spells it. Not the host name above: that is one label this cluster hands out, this is a whole name somebody else owns. Declaring it publishes nothing — it is what a client needs before it can offer to answer to that name locally. Leave empty if the application has no name of its own.',
  'resource.nativeName.placeholder': 'grafana.example.com',
  'resource.field.pin': 'Tool descriptors',
  'resource.pin.label': 'Refuse a tool whose descriptor changed',
  'resource.pin.hint':
    'The first descriptor seen for a tool becomes its baseline, and a call on a tool whose descriptor has changed since is refused until somebody approves the change. Only an MCP server serves descriptors, so the box appears for that kind alone.',
  'resource.whole.title': 'The whole resource is written',
  'resource.whole.body':
    'A write replaces labels, addressing, parent and threshold every time. Anything left empty here is left off the resource, not left as it was.',
  'resource.declared.body':
    'The declaration is written first and the cluster is told second. If the cluster cannot be reached, the declaration stands and arrives with the next application.',
  'resource.notNameable.title': 'Your provider names you in a form the cluster will not record',
  'resource.notNameable.body':
    'Nothing was changed — not here and not in the cluster. gdsgate records the person behind an administrative call and takes a name of letters, digits, spaces and . _ - @ + ’ only.',
  'resource.notNameable.fix':
    'It is put right at your identity provider, by handing out a subject in that form. Trying again changes nothing until it is.',
  'resource.forbidden.title': 'This cluster does not admit you to its catalogue',
  'resource.forbidden.body':
    'The policy no longer names you an administrator of this cluster. The role is recomputed on every call, so this can change under an open session.',
  'resource.remove.title': 'Remove this resource?',
  'resource.remove.subtitle': '{id} goes out of the declared catalogue.',
  'resource.remove.submit': 'Remove',
  'resource.remove.submitting': 'Removing',
  'resource.remove.effect.title': 'What removing does',
  'resource.remove.effect.body':
    'It closes access. A request naming a resource the cluster’s catalogue does not hold is refused before policy is consulted — for everyone, at once.',
  'resource.remove.effect.until':
    'Until the removal reaches the cluster, the cluster still serves it.',
  'resource.gone.title': 'It is not in the catalogue',
  'resource.gone.body':
    'Nothing by that name is declared for this tenancy — it may have been removed already. The list has been read again.',
  'resource.check.idRequired': 'Enter a name.',
  'resource.check.idWhitespace': 'A resource name carries no whitespace.',
  'resource.check.projectRequired': 'Enter a project.',
  'resource.check.environmentRequired': 'Enter an environment.',
  'resource.check.noWhitespace': 'This carries whitespace.',
  'resource.check.portRequired': 'This kind has no well-known port, so a port is required.',
  'resource.check.portRange': 'A port is a whole number between 1 and 65535.',
  'resource.check.approversNumber': 'A whole number, or empty.',
  'resource.check.labelShape': 'One key=value to a line, and neither side empty.',
  'resource.check.labelComma': 'A label carries no comma: the catalogue’s text form does not escape it.',
  'resource.check.labelSpaces':
    'A label carries no two spaces in a row: the catalogue’s text form separates fields by them.',
  'resource.check.nativeName':
    'A whole domain, of at least two labels — app.example.com. One label is a name in this cluster’s own zone, which is the host name above.',

  // ---------- resource kinds ----------
  'resourceKind.ssh': 'SSH',
  'resourceKind.postgres': 'PostgreSQL',
  'resourceKind.mysql': 'MySQL',
  'resourceKind.kubernetes': 'Kubernetes',
  'resourceKind.tcp': 'TCP',
  'resourceKind.mcp': 'MCP',
  'resourceKind.llm': 'Model service',
  'resourceKind.web': 'Web App',
  'resourceKind.webEgress': 'Egress',

  // ---------- attribution ----------
  'attribution.title': 'This is recorded under your name',
  'attribution.body': 'The change lands in the control plane’s own trail as {actor}.',
  'attribution.tenantAudit':
    'Where it reaches the cluster the name goes with it, marked there as claimed by the caller and not verified: a tenancy cannot check which person at the provider asked for a change.',
  'attribution.notNameable':
    'A name the cluster will not record is refused at the edge, and nothing is changed on either side.',

  // ---------- refusals ----------
  'error.network.title': 'No answer from the server',
  'error.network.body': 'The request did not get through. The console changed nothing.',
  'error.unexpected.title': 'An answer this console does not understand',
  'error.unexpected.body':
    'The server refused with something this console has no wording for. Its own sentence is below.',
  'error.unauthenticated.title': 'Not signed in',
  'error.unauthenticated.body':
    'The session is gone or was never there. Signing in again is the whole of it.',
  'error.badCredentials.title': 'Those credentials do not go together',
  'error.badCredentials.body':
    'Which of the two is wrong is deliberately not said: any difference is a way to find out which addresses exist.',
  'error.tooManyAttempts.title': 'Too many attempts',
  'error.tooManyAttempts.body':
    'The window of failed attempts is full. It empties on its own, and the correct password does not get through it in the meantime.',
  'error.tooManyAttempts.wait': 'Try again in {wait}.',
  'error.forbidden.title': 'Not allowed',
  'error.forbidden.body':
    'This role does not carry that at all. It says nothing about which tenancies exist.',
  'error.signInUnavailable.title': 'This console cannot sign anybody in just now',
  'error.signInUnavailable.body':
    'Either no identity provider has been applied to this cluster, or one is being changed, or the provider’s own was chosen and this deployment runs none. The way out of all three is the account.',
  'error.signInExpired.title': 'This sign-in cannot be finished',
  'error.signInExpired.body':
    'It was started in another browser, or too long ago, or it has been finished once already. Nothing is lost and nothing is wrong: start again from this screen.',
  'error.signInRefused.title': 'The identity was not accepted',
  'error.signInRefused.body':
    'What came back from the provider did not pass verification. Which check failed is deliberately not said; the particulars are in the control plane’s log.',
  'error.providerUnreachable.title': 'The identity provider did not answer',
  'error.providerUnreachable.body':
    'It did not reply, or refused for reasons of its own. This is not about you or about what you typed: try again later.',
  'error.notNameable.title': 'Your name is one the cluster will not record',
  'error.notNameable.body':
    'Nothing was changed, here or in the cluster. gdsgate accepts an operator name of letters, digits, spaces and . _ - @ + ’ — a subject in another shape cannot be written to its audit trail.',
  'error.noSuchConsole.title': 'No console answers on this domain',
  'error.noSuchConsole.body':
    'This domain belongs to no tenancy of ours, or this part of the console does not answer on it.',
  'error.tenantNotFound.title': 'No such tenancy',
  'error.tenantNotFound.body':
    'There is no tenancy by that name, or this account is not entitled to it. The answer is the same either way.',
  'error.resourceNotFound.title': 'No such resource',
  'error.resourceNotFound.body': 'It is not in the declared catalogue of this tenancy.',
  'error.accountNotFound.title': 'No such account',
  'error.accountNotFound.body': 'There is no account at that address.',
  'error.invalidRequest.title': 'The server would not take that',
  'error.invalidRequest.body': 'Something in what was sent is not usable. The field is named.',
  'error.providerUnavailable.title': 'There is no provider identity here',
  'error.providerUnavailable.body':
    'This deployment runs no identity provider of its own, so a cluster cannot be pointed at one.',
  'error.identityProviderRefused.title': 'This identity provider will not carry a cluster',
  'error.identityProviderRefused.body':
    'The provider was asked before anything was created, and its answer means the cluster’s control plane would not start on it — or nobody would be able to sign in. Nothing has been created.',
  'error.conflict.title': 'The state does not allow that',
  'error.conflict.body': 'Something is taken, or the current state does not permit the operation.',
  'error.changeUnderWay.title': 'A change is already running',
  'error.changeUnderWay.body':
    'One change of identity provider is under way for this tenancy. A second is refused rather than queued.',
  'error.noFailureToDismiss.title': 'There is nothing to drop',
  'error.noFailureToDismiss.body': 'The last change did not fail, so there is no report to clear.',
  'error.tenantUnreachable.title': 'The tenancy’s cluster did not answer',
  'error.tenantUnreachable.body':
    'The control plane could not reach it, or it refused. Nothing here says the cluster is down for its own users.',
  'nav.cluster.nodes': 'Nodes',
  'nav.cluster.audit': 'Audit',
  'nodes.title': 'Nodes',
  'nodes.subtitle': 'What this cluster has enrolled, read from the cluster itself.',
  'nodes.none': 'Nothing has joined yet',
  'nodes.none.body':
    'A connector joins with a one-time token. Mint one above, then spend it on the machine that will serve your resources.',
  'nodes.filter.label': 'Show',
  'nodes.filter.all': 'All',
  'nodes.filter.active': 'Active',
  'nodes.filter.revoked': 'Revoked',
  'nodes.noMatch': 'Nothing under this tab',
  'nodes.noMatch.body':
    'The registry has nodes, none in this state. A node is active until its renewal is closed, and revoked after — those are the two states a registry row has.',
  'nodes.col.name': 'Name',
  'nodes.col.role': 'Role',
  'nodes.col.id': 'Registry id',
  'nodes.col.enrolled': 'Enrolled',
  'nodes.col.state': 'State',
  'nodes.name.claimed.why':
    'This name is what the node said about itself when it joined. A token is minted for a role, never for a machine, so there is nothing in a name for the cluster to check.',
  'nodes.name.none': 'named itself nothing',
  'nodes.state.active': 'Active',
  'nodes.state.revoked': 'Revoked',
  'nodes.mint': 'Mint a join token',
  'nodes.mint.working': 'Minting',
  'nodes.revoke': 'Revoke',
  'nodes.revoked': 'Node {id} will not renew again',
  'nodes.forget': 'Forget',
  'nodes.forget.working': 'Forgetting',
  'nodes.forgotten': 'Node {id} is off the registry; its revocation stands',
  'nodes.token.title': 'This is the only copy',
  'nodes.token.body':
    'It may be spent once, within {ttl} minutes, and nothing here stores it. Lost it? Mint another — that is cheaper than a console that kept a credential.',
  'nodes.token.label': 'Join token',
  'nodes.token.how':
    'On the machine that will serve your resources: pipe it into `gdsgate join`, on the standard input — never as an argument, which every process on that host can read.',
  'nodes.install': 'Install a connector',
  'nodes.install.working': 'Preparing',
  'nodes.install.client': 'Install the client',
  'nodes.install.title': 'Run this on the machine',
  'nodes.install.body.connector':
    'One command, good for {ttl} minutes. It fetches the binary this tenancy is running, checks it, and registers the connector — asking only for a name and what it serves, both filled in.',
  'nodes.install.body.client':
    'One command, good for {ttl} minutes. It puts the client on that machine; signing in comes after, as whoever runs it.',
  'nodes.install.label': 'Command',
  'nodes.install.how':
    'It needs root only to write `/usr/local/bin/gdsgate`. Registering runs as whoever you are, so several connectors under different accounts on one host is an ordinary thing to do.',
  'offered.title': 'Offered by connectors',
  'offered.subtitle':
    'What your connectors say they serve. Declare one and this cluster starts routing to it.',
  'offered.none': 'No connector is offering anything',
  'offered.allDeclared': 'Everything on offer is declared',
  'offered.allDeclared.body':
    'Every resource your connectors name has a row in your catalogue. A resource leaves this section once you declare it — what is declared belongs on the catalogue screen.',
  'offered.none.body':
    'A connector states what it serves when it connects. Nothing here means no connector is running, or none of them names a backend.',
  'offered.unusable': 'Cannot be declared',
  'offered.unusable.why':
    'A catalogue id is lowercase letters, digits and dashes, at most 63 of them. This connector is offering something else, so no row can be written for it.',
  'offered.unusable.fix':
    'Fix it on the connector: `--serve <id>=<kind>`, then it reappears here',
  'offered.col.resource': 'Resource',
  'offered.col.kind': 'Kind',
  'offered.col.node': 'Offered by',
  'offered.col.offered': 'Last stated',
  'offered.claimed.why':
    'What a connector serves is what it stated when its tunnel opened. The cluster routes to a resource once somebody declares it, and not because a connector named it.',
  'offered.declare': 'Declare',
  'offered.undeclared.title': 'Offered is not reachable',
  'offered.undeclared.body':
    'Until you declare a resource, this cluster routes nothing to it — the connector may be connected and the resource still unreachable. Declaring it writes to your catalogue, with your name on the audit row.',
  'offered.restart.title': 'This list changes when a connector restarts',
  'offered.restart.body':
    'A connector states what it serves from the configuration it started with, and it does not re-read that file while running. A backend you have just added appears here after you restart that connector.',
  'audit.title': 'Audit',
  'audit.subtitle': 'Who did what in this cluster, to what, and how it ended.',
  'audit.read.title': 'Opening this screen is on the record',
  'audit.read.body':
    'Your cluster writes a row for every read of its audit trail, this one included, naming who read it and what they filtered by. That is deliberate: a way of reading the log that left no trace would be the one thing an investigation could not reconstruct.',
  'audit.proof.title': 'This is what your cluster says, not proof of it',
  'audit.proof.body':
    'The trail is a hash chain, and a chain that had been rewritten would answer this screen just as smoothly. Checking that it was not is `gdsgate verify-audit`, run against the cluster’s own database.',
  'audit.col.when': 'When',
  'audit.col.actor': 'Who',
  'audit.actor.agent': 'agent',
  'audit.actor.agent.of': 'agent of {owner}',
  'audit.actor.agent.why':
    'A program acting under somebody\u2019s delegation: their own access, narrowed to what they lent it. Whatever it did here, they could have done.',
  'audit.actor.workload': 'workload',
  'audit.actor.workload.why':
    'A machine identity, not a person. It proved itself with a certificate this cluster issued rather than by signing in.',
  'audit.claimed.label': 'Asserted operator',
  'audit.claimed.why':
    'Who the calling control plane said was behind this. Asserted, never checked — the cluster verified the caller, not this name.',
  'audit.col.class': 'Class',
  'audit.event.label': 'Sealed as',
  'audit.event.why':
    'The name the chain wrote this row under. A cluster writes every policy decision under one name and puts the action it decided beside it, which is the word at the top of this panel.',
  'audit.code.label': 'Code',
  'audit.code.why':
    'What this event is, as a number. Never reused and never renamed, so it means the same thing in a row sealed years apart from this one — unlike the words around it, which have been renamed once already. Rows older than the codes carry none.',
  'audit.session.denied': 'Denied: {reason}',
  'audit.session.label': 'Session',
  'audit.session.timeline': 'This session',
  'audit.session.window':
    'Out of the page that was read. A session that began earlier shows only the part inside it.',
  'audit.col.action': 'Did what',
  'audit.col.target': 'To what',
  'audit.col.outcome': 'Outcome',
  'audit.col.detail': 'Particulars',
  'audit.claimed': 'named by the control plane, not verified',
  'audit.truncated': 'cut to fit',
  'audit.filter.label': 'Class',
  'audit.filter.all': 'Every class',
  'audit.filter.admin-action': 'Administration',
  'audit.filter.authorization': 'Decisions',
  'audit.filter.authentication': 'Sign-ins',
  'audit.filter.session-activity': 'Sessions',
  'audit.filter.resource-integrity': 'Resource integrity',
  'audit.filter.tooNew':
    'This class arrived in gdsgate {version}, and the cluster turned the word itself down: this tenancy runs a build from before it. Nothing was read, every other class still reads, and this tab starts answering once the tenancy is moved to {version} or newer.',
  'audit.filter.tooNew.maybe':
    'This class arrived in gdsgate {version}. A cluster running anything older refuses the whole read rather than answering with no rows — so if this tab alone fails and the others still read, that is the version and not a policy.',
  'audit.actor.label': 'Who',
  'audit.actor.placeholder': 'Anybody',
  'audit.actor.apply': 'Filter',
  'audit.actor.clear': 'Clear',
  'audit.actor.why':
    'Filtering by a person answers “what did they do”, across every resource and every hour. That is what an audit trail is for, and it is also why the right to read one is not the same as the right to administer a cluster.',
  'audit.none': 'Nothing on the trail yet',
  'audit.none.body':
    'A cluster writes its first rows as soon as anybody signs in or a node joins. Nothing here means neither has happened.',
  'audit.noMatch': 'Nothing matches',
  'audit.noMatch.body': 'No row of the page read matches this filter. Widen it, or ask for more rows.',
  'nodes.revoke.means.title': 'What revoking does, and what it does not',
  'nodes.revoke.means.body':
    'It closes renewal: the node cannot get a fresh certificate. The one it already holds stays verifiable until it expires, so a session open right now is not cut by this.',
  'error.clusterRefused.title': 'Your cluster refused',
  'error.clusterRefused.body':
    'The call reached the cluster and it said no. What it said is below, in its own words — a policy that does not allow this, a role that is not running, or a node it does not know.',
  'error.auditClassUnknown.title': 'This cluster does not know that class',
  'error.auditClassUnknown.body':
    'Its gdsgate turned the word down where it takes its arguments, so nothing was read at all — this is not a policy refusing, and not a trail without such rows. Every other class still reads.',
  'error.internal.title': 'The control plane’s storage failed',
  'error.internal.body':
    'The particulars went to its log rather than to this page: a storage message can carry a query, and a query can carry data.',

  'toast.tenantDeclared': '{name} is declared',
  'toast.changeStarted': 'The change has started',
  'toast.resourceDeclared': '{id} is declared and the cluster has it',
  'toast.resourceRemoved': '{id} is removed and the cluster has been told',

  'route.notFound.title': 'No such page',
  'route.notFound.body': 'The address does not match anything in this console.',

  // ---------- stubs ----------
  'stub.banner.title': 'Demonstration data',
  'stub.banner.body':
    'There is no server here. Everything on screen is generated in the browser and nothing reaches a cluster.',
  'stub.entry.account':
    'This page is the account, which in a deployment is served from the provider’s own domain.',
  'stub.entry.cluster':
    'This page is the cluster console of {domain}, which in a deployment is served from that domain.',
  'stub.entry.toCluster': 'Open the cluster console instead',
  'stub.entry.toAccount': 'Open the account instead',
  'stub.accounts.title': 'Demonstration accounts',
  'stub.accounts.password': 'The password for all three is {password}.',
  'stub.accounts.admin': '{email} is a provider operator.',
  'stub.accounts.support': '{email} is the support desk: reads everything, changes nothing.',
  'stub.accounts.tenancy': '{email} represents a tenancy.',
  'stub.accounts.attempts':
    'Five failures against one address fill the window, and the right password stops working with it — which is what a real one does.',
  'stub.identity.title': 'What can be seen here',
  'stub.identity.timing': 'Applying takes about six seconds and finishes by the clock.',
  'stub.identity.rejected':
    'An issuer whose host starts with broken. is refused before anything is touched.',
  'stub.identity.failed':
    'An issuer whose host starts with unstable. passes the check and then does not come back up.',
  'stub.identity.provider':
    'A deployment may run no identity provider of its own, and then that option is not available at all.',
  'stub.identity.provider.off': 'Pretend this deployment runs no provider of its own',
  'stub.identity.provider.on': 'Pretend this deployment runs one',
  'stub.cluster.title': 'The identity provider in this demonstration',
  'stub.cluster.body':
    'There is no provider here, so the demonstration plays one: continuing leaves for a pretend provider that returns to this address with a code and a state, the same way a real one does.',
  'stub.cluster.expired':
    'Opening /login?code=x&state=y by hand is a return nobody started, which is what a spent or foreign code gets.',
  'stub.cluster.vouches': 'What the pretend provider answers:',
  'stub.cluster.as.admin': 'Vouch for somebody in the administrator group',
  'stub.cluster.as.stranger': 'Vouch for somebody in no group of this cluster',
  'stub.cluster.as.refused': 'Answer with an identity that does not verify',
  'stub.cluster.as.unreachable': 'Not answer at all',
  'stub.catalog.title': 'What can be seen here',
  'stub.catalog.body':
    'A write is declared first and carried to the cluster second, and either half can be made to fail.',
  'stub.catalog.reachable.off': 'Pretend the cluster cannot be reached',
  'stub.catalog.reachable.on': 'Pretend it can be reached again',
  'stub.catalog.nameable.off': 'Pretend your provider names you f:71c1e:ivan',
  'stub.catalog.nameable.on': 'Pretend it names you plainly again',
  'stub.usage.title': 'What can be seen here',
  'stub.usage.body':
    'Sessions and people are counted out of the tenancy’s exported audit chain, which the control plane cannot reach here — so by default they arrive missing, with the reason. Switching it on counts them, and one of the counted figures is a genuine zero.',
  'stub.usage.other':
    'Any tenancy other than Acme has no cluster behind it here, so all four figures come back missing.',
  'stub.usage.export.off': 'Pretend the audit export cannot be read',
  'stub.usage.export.on': 'Pretend the audit export can be read',
  'stub.rollout.title': 'What can be seen here',
  'stub.rollout.body':
    'The versions on these cards are readings, not tags: one tenancy has a gateway and a control plane a named break apart, and one had its gateway reading dropped. The connector and the client are unreadable everywhere.',
  'stub.rollout.waves':
    'Acme is the canary and everybody else is the rest. One tenancy in the rest wave fails to move on purpose, so a wave that cannot be signed off can be seen as well as one that can.',
  'stub.rollout.soak':
    'A wave has to stand at its new version before it may be signed off. Here that wait is a switch rather than half an hour.',
  'stub.rollout.soak.off': 'Pretend the wave has not stood long enough',
  'stub.rollout.soak.on': 'Pretend the wave has stood long enough',
  'stub.rollout.repaired':
    'The tenancy that fails goes on failing until somebody has put it right. That visit is the other switch: with it on, moving those again works.',
  'stub.rollout.repaired.off': 'Pretend the tenancy that fails is still broken',
  'stub.rollout.repaired.on': 'Pretend the tenancy that fails has been put right',
} as const;
